import React from 'react';
import { Badge, Modal, Table, Button } from 'react-bootstrap';
import CashSessionSummaryTable from './CashSessionSummaryTable';
import ViewPdfButton from './ViewPdfButton';

const SERVICE_LABELS = {
  TIKANE: 'Ti Kanè',
  TIKANE_SENP: 'Ti Kanè Senp',
  ZELLE: 'Zelle',
  MONCASH: 'MonCash',
  NATCASH: 'NatCash',
  MICRO_CREDIT: 'Micro Crédit',
  EXCHANGE: 'Exchange System',
};

function statusVariant(status) {
  if (status === 'OPEN') return 'success';
  if (status === 'PENDING_CLOSE') return 'warning';
  return 'secondary';
}

export default function CashSessionDetailModal({ show, onHide, session }) {
  const requests = session?.requests || [];
  const movements = session?.movements || [];

  const getDocument = () => ({
    title: `Détails session de caisse${session?.id ? ` #${session.id}` : ''}`,
    sections: [
      {
        title: 'Session',
        rows: [
          { label: 'Utilisateur', value: session?.user?.username || '—' },
          { label: 'Statut', value: session?.status || '—' },
          {
            label: 'Ouverte le',
            value: session?.openedAt ? new Date(session.openedAt).toLocaleString() : '—',
          },
          {
            label: 'Fermée le',
            value: session?.closedAt ? new Date(session.closedAt).toLocaleString() : '—',
          },
        ],
      },
      {
        title: 'Mouvements / transactions',
        rows:
          movements.length > 0
            ? movements.map((row, idx) => ({
                label: `Mouvement ${idx + 1}`,
                value: `Date: ${
                  row.performedAt ? new Date(row.performedAt).toLocaleString() : '—'
                } | Module: ${SERVICE_LABELS[row.serviceKey] || row.serviceKey} | Type: ${
                  row.movementType
                } | Devise: ${row.currency} | Montant: ${Number(row.amount || 0).toFixed(
                  2
                )} | Libellé: ${row.label || '—'}`,
              }))
            : [{ label: 'Mouvements', value: 'Aucun mouvement.' }],
      },
      {
        title: 'Demandes liées à la session',
        rows:
          requests.length > 0
            ? requests.map((row, idx) => ({
                label: `Demande ${idx + 1}`,
                value: `Date: ${
                  row.requestedAt ? new Date(row.requestedAt).toLocaleString() : '—'
                } | Type: ${row.requestType} | Statut: ${row.status} | Demandé par: ${
                  row.requestedByUser?.username || session?.user?.username || '—'
                } | Traité par: ${
                  row.approvedByUser?.username || row.rejectedByUser?.username || '—'
                } | Note: ${row.decisionNote || row.requesterNote || '—'}`,
              }))
            : [{ label: 'Demandes', value: 'Aucune demande liée.' }],
      },
    ],
  });

  return (
    <Modal show={show} onHide={onHide} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>Détails de la session de caisse</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!session ? (
          <div className="text-muted">Chargement…</div>
        ) : (
          <>
            <div className="d-flex flex-wrap gap-4 mb-3">
              <div><strong>Utilisateur</strong><div>{session.user?.username || '—'}</div></div>
              <div><strong>Statut</strong><div><Badge bg={statusVariant(session.status)}>{session.status}</Badge></div></div>
              <div><strong>Ouverte le</strong><div>{session.openedAt ? new Date(session.openedAt).toLocaleString() : '—'}</div></div>
              <div><strong>Fermée le</strong><div>{session.closedAt ? new Date(session.closedAt).toLocaleString() : '—'}</div></div>
            </div>

            <CashSessionSummaryTable session={session} showRequesterClose />

            <h5 className="mt-4">Mouvements / transactions</h5>
            <Table hover responsive size="sm">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Module</th>
                  <th>Type</th>
                  <th>Devise</th>
                  <th>Montant</th>
                  <th>Libellé</th>
                </tr>
              </thead>
              <tbody>
                {movements.length === 0 ? (
                  <tr><td colSpan={6} className="text-center text-muted">Aucun mouvement.</td></tr>
                ) : movements.map((row) => (
                  <tr key={row.id}>
                    <td>{row.performedAt ? new Date(row.performedAt).toLocaleString() : '—'}</td>
                    <td>{SERVICE_LABELS[row.serviceKey] || row.serviceKey}</td>
                    <td>{row.movementType}</td>
                    <td>{row.currency}</td>
                    <td>{Number(row.amount || 0).toFixed(2)}</td>
                    <td>{row.label || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <h5 className="mt-4">Demandes liées à la session</h5>
            <Table hover responsive size="sm">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Statut</th>
                  <th>Demandé par</th>
                  <th>Traité par</th>
                  <th>Note</th>
                </tr>
              </thead>
              <tbody>
                {requests.length === 0 ? (
                  <tr><td colSpan={6} className="text-center text-muted">Aucune demande liée.</td></tr>
                ) : requests.map((row) => (
                  <tr key={row.id}>
                    <td>{row.requestedAt ? new Date(row.requestedAt).toLocaleString() : '—'}</td>
                    <td>{row.requestType}</td>
                    <td>{row.status}</td>
                    <td>{row.requestedByUser?.username || session.user?.username || '—'}</td>
                    <td>{row.approvedByUser?.username || row.rejectedByUser?.username || '—'}</td>
                    <td>{row.decisionNote || row.requesterNote || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        {session && <ViewPdfButton getDocument={getDocument} className="me-2" />}
        <Button variant="secondary" onClick={onHide}>Fermer</Button>
      </Modal.Footer>
    </Modal>
  );
}